import { useEffect, useRef, useState } from 'react';
import type { Order } from '@/types/order';
import { ApiRequestError } from '@/api';
import { postPreviewInvoice, postSendInvoice } from '@/api/modules/order';

interface InvoiceOrderModalProps {
  order: Order | null;
  onClose: () => void;
  onInvoiceSent?: (order: Order) => void;
  onConfirmPayment?: (order: Order) => void;
}

function base64ToBlobUrl(base64: string, mimeType: string): string {
  const trimmed = base64.trim();
  let binary: string;
  try {
    binary = atob(trimmed);
  } catch {
    return '';
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: mimeType });
  return URL.createObjectURL(blob);
}

function base64ToDataUrl(base64: string, mimeType: string): string {
  const trimmed = base64.trim();
  if (trimmed.startsWith('data:')) return trimmed;
  return `data:${mimeType};base64,${trimmed}`;
}

export default function InvoiceOrderModal({
  order,
  onClose,
  onInvoiceSent,
  onConfirmPayment,
}: InvoiceOrderModalProps) {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');
  const [invoiceBase64, setInvoiceBase64] = useState('');
  const [invoiceType, setInvoiceType] = useState<'pdf' | 'image'>('pdf');
  const [invoiceNumber, setInvoiceNumber] = useState<string | undefined>();
  const [previewBlobUrl, setPreviewBlobUrl] = useState('');
  const [embedFailed, setEmbedFailed] = useState(false);

  const blobUrlRef = useRef<string>('');

  const mimeType = invoiceType === 'pdf' ? 'application/pdf' : 'image/jpeg';

  // 管理 blob URL 生命周期：在 useEffect 中创建/清理，避免 Strict Mode 双调用导致的提前回收
  useEffect(() => {
    if (!invoiceBase64) {
      setPreviewBlobUrl('');
      return;
    }
    // 先回收旧 URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = '';
    }
    const url = base64ToBlobUrl(invoiceBase64, mimeType);
    blobUrlRef.current = url;
    setPreviewBlobUrl(url);
    setEmbedFailed(false);

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = '';
      }
    };
  }, [invoiceBase64, mimeType]);

  useEffect(() => {
    if (!order) {
      setInvoiceBase64('');
      setInvoiceType('pdf');
      setInvoiceNumber(undefined);
      setPreviewError('');
      setSendError('');
      setSendSuccess('');
      setEmbedFailed(false);
      return;
    }

    let cancelled = false;
    setLoadingPreview(true);
    setPreviewError('');
    setSendError('');
    setSendSuccess('');
    setInvoiceBase64('');
    setInvoiceType('pdf');
    setInvoiceNumber(undefined);
    setEmbedFailed(false);

    postPreviewInvoice(order.id)
      .then((result) => {
        if (cancelled) return;
        setInvoiceBase64(result.invoiceBase64);
        setInvoiceType(result.invoiceType ?? 'pdf');
        setInvoiceNumber(result.invoiceNumber);
      })
      .catch((e) => {
        if (cancelled) return;
        setPreviewError(
          e instanceof ApiRequestError ? e.message : 'Failed to preview invoice.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });

    return () => {
      cancelled = true;
    };
  }, [order?.id]);

  useEffect(() => {
    if (!order) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [order]);

  if (!order) return null;

  const customerEmail = order.customer.email?.trim();
  const canSend = Boolean(customerEmail && customerEmail !== '—' && invoiceBase64 && !loadingPreview);

  const handleSend = async () => {
    if (!canSend || sending) return;
    setSending(true);
    setSendError('');
    setSendSuccess('');
    try {
      const result = await postSendInvoice(order.id);
      setSendSuccess(result.message);
      if (result.invoiceNumber) setInvoiceNumber(result.invoiceNumber);
      onInvoiceSent?.({ ...order, invoiceSent: true });
    } catch (e) {
      setSendError(e instanceof ApiRequestError ? e.message : 'Failed to send invoice.');
    } finally {
      setSending(false);
    }
  };

  const handleDownload = () => {
    if (!previewBlobUrl) return;
    const link = document.createElement('a');
    link.href = previewBlobUrl;
    const date = new Date().toISOString().slice(0, 10);
    const ext = invoiceType === 'pdf' ? 'pdf' : 'jpg';
    link.download = `Invoice_Order${order.id}_${date}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewWindow = () => {
    if (!previewBlobUrl && !invoiceBase64) return;
    // 优先使用 blob URL（兼容性最好），fallback 到 data URL
    const url = previewBlobUrl || base64ToDataUrl(invoiceBase64, mimeType);
    const win = window.open(url, '_blank');
    if (!win) {
      // 弹窗被拦截，尝试下载
      handleDownload();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-white flex flex-col"
      style={{ maxWidth: 480, margin: '0 auto' }}
      role="dialog"
      aria-modal="true"
      aria-label="Invoice"
    >
      <div className="flex-shrink-0 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-2.5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 cursor-pointer flex-shrink-0 hover:bg-gray-200 transition-colors"
            aria-label="Close invoice modal"
          >
            <i className="ri-arrow-left-s-line text-gray-700 text-xl" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-gray-800 font-bold text-base truncate">Invoice</div>
            <p className="text-gray-400 text-[11px] truncate">
              {invoiceNumber ? `Invoice No. ${invoiceNumber}` : order.id}
            </p>
          </div>
          {!loadingPreview && previewBlobUrl ? (
            <button
              type="button"
              onClick={handleDownload}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 cursor-pointer flex-shrink-0 hover:bg-gray-200 transition-colors"
              aria-label="Download invoice"
            >
              <i className="ri-download-2-line text-gray-600" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="px-3 py-3 overflow-y-auto flex-1 min-h-0 space-y-3 overscroll-contain scrollbar-minimal">
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 text-xs space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-500 font-medium">Customer</span>
              <span className="text-gray-800 font-semibold truncate text-right">
                {order.customer.name}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-500 font-medium">Email</span>
              <span className="text-gray-700 truncate text-right">
                {customerEmail && customerEmail !== '—' ? customerEmail : '—'}
              </span>
            </div>
          </div>

          {loadingPreview && (
            <div className="py-12 text-center">
              <i className="ri-loader-4-line animate-spin text-green-500 text-2xl" />
              <p className="text-gray-400 text-xs mt-2">Generating invoice preview…</p>
            </div>
          )}

          {previewError && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {previewError}
            </div>
          )}

          {/* PDF 预览区域 */}
          {!loadingPreview && previewBlobUrl && invoiceType === 'pdf' && !embedFailed && (
            <div className="rounded-lg border border-gray-200 bg-gray-100 overflow-hidden">
              <object
                data={previewBlobUrl}
                type="application/pdf"
                width="100%"
                className="bg-white block w-full"
                style={{ height: 'calc(100dvh - 14rem)' }}
              >
                {/* object 不支持时的 fallback */}
                <iframe
                  src={previewBlobUrl}
                  title={`Invoice for order ${order.id}`}
                  className="w-full bg-white block"
                  style={{ height: 'calc(100dvh - 14rem)' }}
                  onError={() => setEmbedFailed(true)}
                />
              </object>
            </div>
          )}

          {/* 图片预览（兼容旧格式） */}
          {!loadingPreview && previewBlobUrl && invoiceType !== 'pdf' && (
            <div className="rounded-lg border border-gray-200 bg-gray-100 overflow-hidden">
              <img
                src={previewBlobUrl}
                alt={`Invoice for order ${order.id}`}
                className="w-full h-auto block"
              />
            </div>
          )}

          {/* inline 预览失败时的 fallback 卡片 */}
          {!loadingPreview && previewBlobUrl && invoiceType === 'pdf' && embedFailed && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center space-y-3">
              <i className="ri-file-pdf-2-line text-5xl text-amber-500" />
              <p className="text-sm text-amber-800">
                PDF preview is not available in this browser.
              </p>
              <button
                type="button"
                onClick={handleOpenInNewWindow}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-amber-500 text-white text-sm font-semibold cursor-pointer hover:bg-amber-600"
              >
                <i className="ri-external-link-line" />
                Open PDF in New Window
              </button>
            </div>
          )}

          {/* 新窗口打开按钮（始终显示，方便用户操作） */}
          {!loadingPreview && previewBlobUrl && (
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={handleOpenInNewWindow}
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 cursor-pointer py-1"
              >
                <i className="ri-external-link-line" />
                Open in new window
              </button>
              {invoiceType === 'pdf' && !embedFailed && (
                <button
                  type="button"
                  onClick={() => setEmbedFailed(true)}
                  className="text-xs text-gray-400 hover:text-gray-500 cursor-pointer py-1"
                >
                  Having trouble viewing?
                </button>
              )}
            </div>
          )}

          {sendError && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {sendError}
            </div>
          )}
          {sendSuccess && (
            <div className="text-xs text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
              {sendSuccess}
            </div>
          )}
        </div>

        <div className="p-3 pt-3 border-t border-gray-100 flex-shrink-0 space-y-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={!previewBlobUrl || loadingPreview}
              className="flex-1 h-11 rounded-lg border border-gray-200 text-gray-700 text-sm font-normal cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
            >
              <i className="ri-download-2-line" />
              Download
            </button>
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={!canSend || sending}
              className="flex-1 h-11 rounded-lg bg-green-600 text-white text-sm font-normal cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
            >
              {sending ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <i className="ri-mail-send-line" />
                  Send Email
                </>
              )}
            </button>
          </div>
          {sendSuccess && onConfirmPayment && order.payStatus !== 1 && (order.status === 'service_done' || order.status === 'completed') ? (
            <button
              type="button"
              onClick={() => onConfirmPayment({ ...order, invoiceSent: true })}
              className="w-full h-11 rounded-lg bg-teal-600 text-white text-sm font-semibold"
            >
              Confirm Payment
            </button>
          ) : null}
          {!customerEmail || customerEmail === '—' ? (
            <p className="text-[11px] text-amber-600 text-center">
              Customer email is missing. Cannot send invoice.
            </p>
          ) : (
            <p className="text-[11px] text-gray-400 text-center">
              Invoice will be sent to {customerEmail}
            </p>
          )}
        </div>
    </div>
  );
}
